import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { keys } from "@/lib/api/query-keys";
import type {
  ChatConversationPayload,
  ChatMessageChangedPayload,
  ChatMessagePayload,
  ChatReadPayload,
  ChatTypingPayload,
} from "@/lib/realtime/realtime-events";
import { useRealtimeEvent } from "@/lib/realtime/useRealtime";
import { MESSAGE_PAGE_SIZE } from "../api/chat.queries";
import { COALESCE_MS, applyInvalidation, keyOf, type Invalidation } from "./chat-invalidations";
import { typingStore } from "./typing-store";

/**
 * THE ONE PLACE CHAT LISTENS TO THE STREAM.
 *
 * Before this, three surfaces subscribed at once — the dock, the thread hook and the page — so a
 * single `chat.message` in an open conversation cost three identical invalidations and, through
 * the `["chat","conversations"]` prefix, a refetch of every conversation DETAIL in the cache as
 * well. On a busy group that is the load the audit measured growing with the number of members.
 *
 * Mounted once in `AppLayout`, beside `RealtimeGate`. It renders nothing and holds no state: what
 * it does is decide which query is asked to go and re-read.
 *
 * THREE RULES, and each one is a request that used to be made and no longer is:
 *
 *  1. **Narrow, not by prefix.** The rail is `["chat","conversations",{filters}]` and a detail is
 *     `["chat","conversations","detail",id]`; invalidating the prefix hits both. A message changes
 *     the rail, not the roster, so the predicate skips `detail`.
 *  2. **Coalesced.** Frames arrive in bursts (a group of twenty, a paste of five lines). Keys are
 *     collected for `COALESCE_MS` and invalidated once, so five frames are one request per key
 *     rather than five.
 *  3. **Only the thread that is on screen.** A conversation nobody is looking at is not refetched;
 *     its query is marked stale and re-reads itself when it is opened (`refetchType: "active"` is
 *     the default of `invalidateQueries`, and this asks explicitly).
 */

export function ChatRealtimeBridge({ meId }: { meId: string | null }) {
  const queryClient = useQueryClient();
  const pending = useRef(new Map<string, Invalidation>());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const flush = useMemo(
    () => () => {
      timer.current = undefined;
      const batch = [...pending.current.values()];
      pending.current.clear();
      for (const invalidation of batch) applyInvalidation(queryClient, invalidation);
    },
    [queryClient],
  );

  const schedule = useMemo(
    () => (invalidation: Invalidation) => {
      pending.current.set(keyOf(invalidation), invalidation);
      if (timer.current !== undefined) return;
      timer.current = setTimeout(flush, COALESCE_MS);
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
    },
    [],
  );

  /**
   * Is that conversation the one on screen? Asked of the CACHE rather than of a prop: the thread
   * lives in the dock on one screen and in the page on another, and a query with an observer is
   * exactly "somebody is showing this".
   */
  const isOnScreen = useMemo(
    () => (conversationId: string) =>
      (queryClient
        .getQueryCache()
        .find({ queryKey: keys.chat.messages(conversationId, { limit: MESSAGE_PAGE_SIZE }) })
        ?.getObserversCount() ?? 0) > 0,
    [queryClient],
  );

  useRealtimeEvent<ChatMessagePayload>("chat.message", (frame) => {
    const { conversationId, senderUserId } = frame.data;
    // Their message arrived: whatever they were typing, they are not typing it any more.
    typingStore.clear(conversationId, senderUserId);
    if (isOnScreen(conversationId)) schedule({ kind: "thread", conversationId });
    schedule({ kind: "list" });
    schedule({ kind: "unread" });
  });

  /**
   * An author edited or redacted a message. Both carry only ids, so both are answered the same
   * way: refetch the thread. The rail goes with it because its last-message preview is derived
   * from that message — a redacted one must stop showing its own text there too, which is the
   * leak a thread-only refresh would leave on screen.
   */
  const changed = (conversationId: string) => {
    if (isOnScreen(conversationId)) schedule({ kind: "thread", conversationId });
    schedule({ kind: "list" });
  };

  useRealtimeEvent<ChatMessageChangedPayload>("chat.message.edited", (frame) => changed(frame.data.conversationId));
  useRealtimeEvent<ChatMessageChangedPayload>("chat.message.redacted", (frame) => changed(frame.data.conversationId));

  useRealtimeEvent<ChatReadPayload>("chat.read", (frame) => {
    /**
     * WHOSE CURSOR MOVED decides what is re-read, and the two answers have nothing in common.
     *
     * Mine, from another tab or my phone: my unread counters changed — the rail and the badge.
     * The other person's, in a DIRECT conversation (K6: never in a group): what changed is the
     * «Okundu» mark on MY messages, which lives in that conversation's detail (`counterpartRead`).
     * The rail knows nothing about it, and asking it was a request per read receipt — on a busy
     * conversation, one per message.
     */
    if (frame.data.userId === meId) {
      schedule({ kind: "list" });
      schedule({ kind: "unread" });
      // H4 (20 Ekim 2026): the read settled my bell rows about that conversation — this tab's
      // bell follows the one I read in. One count request per burst (coalesced).
      schedule({ kind: "bell" });
      return;
    }
    schedule({ kind: "detail", conversationId: frame.data.conversationId });
  });

  useRealtimeEvent<ChatConversationPayload>("chat.conversation", (frame) => {
    const { conversationId } = frame.data;
    schedule({ kind: "list" });
    schedule({ kind: "detail", conversationId });
    // Membership and history visibility changed with it: the transcript may now show more or less.
    if (isOnScreen(conversationId)) schedule({ kind: "thread", conversationId });
  });

  useRealtimeEvent<ChatTypingPayload>("chat.typing", (frame) => {
    // Your own keystrokes come back to you on the stream; showing "Siz yazıyor…" would be absurd.
    if (frame.data.userId === meId) return;
    typingStore.note(frame.data.conversationId, frame.data.userId);
  });

  return null;
}
